import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useQuery } from "@apollo/client/react";
import { useMsal } from "@azure/msal-react";
import { ApiRole, DashboardData, Role, RolesData } from "../context/model";
import { DASHBOARD_RECORD_QUERY, ROLES_QUERY, ROLES_QUERY_LIST } from "../services/query";
import { fetchDataAndIndex } from "../services/searchquery";
import { initializeLocalizationService } from "../services/localizationService";
import { getAllRoles } from "../services/resourcesService";
import { useGlobalContext } from "./GlobalContextProvider";
import { silentTokenAcquire } from "../services/silentTokenAcquire";
import { useSilentTokenAcquire } from "../hooks/useSilentTokenAcquire";
const MAX_RELOGIN_ATTEMPTS = 2;

export interface IAppContext {
  userName: string;
  roleName: string;
  setUserName: React.Dispatch<React.SetStateAction<string>>;
  token: string;
  setToken: React.Dispatch<React.SetStateAction<string>>;
  roles: string[];
  loader: string[];
  updateLoading: (isLoading: boolean, loaderName: string) => void;
  dashboardId: string;
  dashboardListId: string[];
  isInitializing: boolean;
  rightHandSectionTitleMap: Record<string, string>;
  priorityDashboard: string;
}

const defaultState = {} as IAppContext;
const AppContext = createContext<IAppContext>(defaultState);
export const useAppContext = (): IAppContext => useContext(AppContext);

const AppContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const { updateGLoading } = useGlobalContext();

  const [loader, setLoader] = useState<string[]>([]);
  const [token, setToken] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [roles, setRoles] = useState<string[]>([]);
  const [roleName, setRoleName] = useState<string>('');
  const [dashboardId, setDashboardId] = useState<string>('');
  const [dashboardListId, setDashboardListId] = useState<string[]>([]);
  const [rightHandSectionTitleMap, setRightHandSectionTitleMap] = useState<Record<string, string>>({});
  const [priorityDashboard, setPriorityDashboard] = useState<string>("");
  const reloginAttemptsRef = useRef(0);

  const [isInitializing, setIsInitializing] = useState(true);
  // const [roleData, setRoleData] = useState<Role[]>([]);
  const [rolesData, setRolesData] = useState<ApiRole[]>([]);

  // const silentTokenAcquireHook = useSilentTokenAcquire(); // <-- Call hook here


  // const { error: rolesError, data: rolesData, loading: rolesLoading } = useQuery<RolesData>(ROLES_QUERY_LIST, {
  //   skip: roles.length === 0 || isInitializing,
  //   variables: { roleValues: roles },
  // }); //'GG_WU_Portal_CA_Marketing_West'
  const isDevelopment = process.env.REACT_APP_ENV === 'localDevelopment';

  const { instance } = useMsal();
  const account = instance.getAllAccounts()[0];

  // Ensure we have an active account to prevent cache iteration errors
  useEffect(() => {
    if (account && !instance.getActiveAccount()) {
      instance.setActiveAccount(account);
    }
  }, [account, instance]);

  //Memoize to avoid triggering effect on every render
  const userRole = useMemo(() => {
    return (account?.idTokenClaims?.security_roles as string[]) || [];
  }, [account]);

  const {
    error: dashboardError,
    data: dashboardData,
    loading: dashboardLoading,
  } = useQuery<DashboardData>(DASHBOARD_RECORD_QUERY, {
    skip: isInitializing || dashboardListId.length === 0,
    variables: { dashboardListId },
  });

  useEffect(() => {
    if (dashboardError) {
      console.error('Error fetching dashboard data:', dashboardError);
      setRightHandSectionTitleMap({});
      return;
    }
    if (dashboardData && dashboardData?.cADashboardCollection?.items?.length > 0) {
      const titleMap: Record<string, string> = {};
      let highestPriorityDashboardId = "";
      let lowestPriorityValue = Number.MAX_SAFE_INTEGER;
      dashboardData.cADashboardCollection.items.forEach(item => {
        const priority = item.priority || Number.MAX_SAFE_INTEGER;
        // Find dashboard with lowest priority number (highest priority, 1 is highest)
        if (priority < lowestPriorityValue) {
          lowestPriorityValue = priority;
          highestPriorityDashboardId = item._system.id;
        }
        titleMap[`academyCalendarTitle`] = item.academyCalendarTitle || "";
      });

      setRightHandSectionTitleMap(titleMap);
      setPriorityDashboard(highestPriorityDashboardId);
    }
  }, [dashboardData, dashboardError]);

  const getAllRolesData = async () => {
    try {
      const response: any = await getAllRoles();
      reloginAttemptsRef.current = 0; // Reset on success
      return response;
    } catch (error: any) {
      console.info("Error fetching getAllRolesData:", error);
      if (
        (error?.response?.status === 401 || error?.response?.status === 403) &&
        reloginAttemptsRef.current < MAX_RELOGIN_ATTEMPTS
      ) {
        reloginAttemptsRef.current++;
        const isReLogined = await silentTokenAcquire(instance);
        if (isReLogined) {
          const response: any = await getAllRoles();
          return response;
        }
      } else {
        console.log(error);
        reloginAttemptsRef.current = 0; // Reset on other errors
        return null;
      }
    }
  };

  const getDashboardListId = async () => {
    updateGLoading(true, "globalLoaderAppcontext");
    try {
      const allRoles: any = await getAllRolesData();
      if (allRoles && allRoles.data) {
        const rolesList: ApiRole[] = allRoles.data;
        setRolesData((prev) => (JSON.stringify(prev) !== JSON.stringify(rolesList) ? rolesList : prev));
      } else {
        setRolesData([]);
      }

    } catch (error) {
      console.log('Error fetching roles data:', error);
      setRolesData([]);
    } finally {
      console.log('Finished fetching roles data');
      setIsInitializing(false);
      updateGLoading(false, "globalLoader");
      updateGLoading(false, "globalLoaderAppcontext");
    }
  };

  // Effect to handle user role changes and initialization

  useEffect(() => {
    if (account && userRole.length > 0) {
      sessionStorage.setItem("authToken", account?.idToken || ""); // switched from sessionStorage
      setRoles((prev) => (prev.join() !== userRole.join() ? userRole : prev));
      if (roles.join() !== userRole.join()) {
        fetchDataAndIndex();
        getDashboardListId();
      }
    }
  }, [userRole]); // Now this only fires when memoized userRole or account changes

  // Set roles data once roles are initialized
  // useEffect(() => {
  //   if (rolesData && rolesData.cARolesCollection) {
  //     setRoleData((prev) => (JSON.stringify(prev) !== JSON.stringify(rolesData.cARolesCollection.items || []) ? rolesData.cARolesCollection.items || [] : prev));
  //     // setInitialized(true);
  //   }
  // }, [roles, rolesData]);

  // Determine dashboard ID based on role data
  useEffect(() => {

    if (rolesData.length > 0) {
      const displayRolesName: string = rolesData
        .map(role => role.dashBoardDisplayName)
        .join(", ");
      setRoleName(displayRolesName);
      // const dashboardItems = roleData[0]?.dashboard.items || [];
      // const roleId = dashboardItems[0]?._system.id || '';
      // setDashboardId((prev) => (prev !== roleId ? roleId : prev));

      const dashboardSystemIds = rolesData.map(role =>
        role.dashboardID
      );
      const uniqueDashboardIds = Array.from(new Set(dashboardSystemIds));
      setDashboardListId((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(uniqueDashboardIds)) {
          return uniqueDashboardIds;
        }
        return prev;
      });
      // setIsInitializing(false);
    } else {
      if (isDevelopment) {
        setDashboardListId(['83a34204-efda-4861-ae6d-b1248ea87ccd']);
        fetchDataAndIndex();
      } else {
        setDashboardListId([]);
      }
      // setDashboardId('');
      // setDashboardListId([]);
    }
  }, [rolesData]);

  // Update loading state with callback to ensure state consistency
  const updateLoading = useCallback((isLoading: boolean, loaderName: string) => {
    setLoader((prevLoader) => {
      if (isLoading && !prevLoader.includes(loaderName)) {
        return [...prevLoader, loaderName];
      }
      if (!isLoading && prevLoader.includes(loaderName)) {
        return prevLoader.filter((item) => item !== loaderName);
      }
      return prevLoader;
    });
  }, []);

  // Memoized state for context value
  const state = useMemo(
    () => ({
      userName,
      setUserName,
      token,
      setToken,
      roles,
      roleName,
      loader,
      updateLoading,
      dashboardId,
      dashboardListId,
      isInitializing,
      rightHandSectionTitleMap,
      priorityDashboard
    }),
    [userName, token, roles, roleName, loader, updateLoading, dashboardId]
  );

  // Initialize localization service after app loading is complete
  useEffect(() => {
    if (!isInitializing) {
      initializeLocalizationService().catch(error => {
        console.error('Failed to initialize localization service:', error);
      });
    }
  }, [isInitializing]);

  return <AppContext.Provider value={state}>{children}</AppContext.Provider>;
};

export default AppContextProvider;